import { useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './client';
import { subscribeConversationChanges } from './conversationInvalidation';

const PAGE_SIZE = 50;

export async function hasUnreadMessages(
  client: SupabaseClient,
  userId: string,
  signal: AbortSignal,
) {
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const { data, error } = await client
      .from('conversations')
      .select('id,conversation_participants!inner(last_read_at),messages(created_at)')
      .eq('conversation_participants.user_id', userId)
      .is('conversation_participants.hidden_at', null)
      .in('status', ['pending', 'accepted'])
      .or(`user_id.neq.${userId},user_id.is.null`, { referencedTable: 'messages' })
      .order('id')
      .order('created_at', { referencedTable: 'messages', ascending: false })
      .limit(1, { referencedTable: 'messages' })
      .range(offset, offset + PAGE_SIZE - 1)
      .abortSignal(signal);
    if (error) throw new Error(error.message);
    if (
      data.some(({ conversation_participants, messages }) => {
        const newest = messages[0]?.created_at;
        const lastRead = conversation_participants[0]?.last_read_at;
        return newest && (!lastRead || newest > lastRead);
      })
    )
      return true;
    if (data.length < PAGE_SIZE) return false;
  }
}

export function useUnreadMessages(userId: string | undefined, unreadConversations: string) {
  const [result, setResult] = useState<{ userId: string; unread: boolean }>();
  useEffect(() => {
    const client = supabase;
    if (!client || !userId) return;
    let request: AbortController | undefined;
    const refresh = () => {
      request?.abort();
      const controller = new AbortController();
      request = controller;
      void hasUnreadMessages(client, userId, controller.signal)
        .then((unread) => {
          if (!controller.signal.aborted) setResult({ userId, unread });
        })
        .catch(() => {
          // Keep the last successful result until the next inbox refresh.
        });
    };
    const unsubscribe = subscribeConversationChanges(userId, refresh);
    refresh();
    return () => {
      request?.abort();
      unsubscribe();
    };
  }, [userId, unreadConversations]);
  return result?.userId === userId && result?.unread === true;
}
